/* eslint-disable @typescript-eslint/no-explicit-any */
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { Stack } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import FileUploader from 'components/ui/file-uploader/FileUploader';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import productStatusCell from 'components/cells/product-status-cell';
import { filterOperators, dateFilterOperators } from 'components/enhanced';

export function ContractLinesAllColumns(service: any): GridColDef[] {
  const { t } = useTranslation();
  const translationPrefix = 'entities.movement';

  const columns: GridColDef[] | any[] = [
    {
      field: 'code',
      headerName: t(`global.code`),
      filterOperators,
      renderCell: (params: GridRenderCellParams) => {
        const contract = params.row;
        return (
          <div>
            <Link to={`${contract.id}`}>
              <b className="text-secondary-500">{contract.code}</b>
            </Link>
          </div>
        );
      }
    },
    { field: 'supplier', filterOperators, headerName: t(`global.supplier`), flex: 1 },
    {
      field: 'laboratories',
      filterOperators,
      headerName: t(`global.laboratories`),
      valueGetter: ({ row }) => row.laboratory_codes,
      flex: 1
    },
    {
      field: 'active',
      type: 'boolean',
      headerName: t(`global.active`),
      width: 100,
      valueGetter: ({ value }) => (value ? 1 : 2),
      valueFormatter: ({ value }) => t(`entities.product.status_options.${value}`),
      renderCell: productStatusCell(t)
    },
    {
      field: 'tender_ref',
      filterOperators,
      headerName: t(`entities.contract.tender_ref`),
      flex: 1
    },
    {
      field: 'commission_ref',
      filterOperators,
      headerName: t(`entities.contract.commission_ref`),
      flex: 1
    },
    {
      field: 'drafting_date',
      filterOperators: dateFilterOperators,
      type: 'date',
      headerName: t(`entities.contract.drafting_date`),
      flex: 1,
      valueGetter: ({ value }) => (value ? new Date(value) : null)
    },
    {
      field: 'expiration_date',
      headerName: t(`entities.contract.expiration_date`),
      type: 'date',
      filterOperators: dateFilterOperators,
      flex: 1,
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      valueFormatter: ({ value }) => value && dayjs(value).format('DD/MM/YYYY'),
      renderCell: (params: GridRenderCellParams<any, Date>) => {
        const { value, formattedValue } = params;

        const isExpiringSoon = value && dayjs(value).diff(dayjs(), 'month', true) < 3;

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            {isExpiringSoon && value && <NewReleasesIcon color="warning" fontSize="small" />}
            <span>{formattedValue?.toString()}</span>
          </Stack>
        );
      }
    },
    {
      field: 'commercial_bid',
      headerName: t(`entities.contract.commercial_bid`),
      renderCell: (params: GridRenderCellParams) => {
        const contract = params.row;
        return (
          <IsTenantAdministrator>
            <div>
              <FileUploader
                service={service}
                entityId={contract.id}
                attachmentName={'commercial_bid'}
                filename={contract.commercial_bid}
              />
            </div>
          </IsTenantAdministrator>
        );
      },
      width: 70,
      filterable: false,
      sortable: false
    },
    {
      field: 'bid_approval',
      headerName: t(`entities.contract.bid_approval`),
      renderCell: (params: GridRenderCellParams) => {
        const contract = params.row;
        return (
          <IsTenantAdministrator>
            <div>
              <FileUploader
                service={service}
                entityId={contract.id}
                attachmentName={'bid_approval'}
                filename={contract.bid_approval}
              />
            </div>
          </IsTenantAdministrator>
        );
      },
      width: 70,
      filterable: false,
      sortable: false
    }
  ];

  return columns;
}
